import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { parseDateToLgComun4 } from "../../../../helpers/dateTransform";
import EGAccordion from "../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import Comments from "./Comments";
import { Complements } from "./Complements";
import { AdvisorReview } from "./AdvisorReview";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { IconHeader } from "../../../Icon/IconHader/IconHeader";
import "./Information.scss";
import { detailAdvisorReview } from "../../../../apis/apiAdvisorReview";
import { useEffect, useState } from "react";

// Quita la numeración del inicio del label (ej: "01 Pedro" -> "Pedro")
const stripLabelNumbering = (label) => {
  if (!label || typeof label !== 'string') return label;
  return label.replace(/^\d+\s+/, '');
};

export const InvestigationSummary = ({
  reportDetail,
  setShowModalAttachments,
}) => {
  const { lang } = useSelector((state) => state.lang);
  const [t] = useTranslation("information");
  const theme = useTheme();

  const involvedOrder = {
    victim: 1,
    witness: 2,
    offender: 3,
    accomplice: 4,
  };

  const [isShown, setIsShown] = useState(false);


  const formatDateValue = (value) => {
    // Si no es string, retornar el valor original
    if (typeof value !== 'string') {
      return value;
    }

    // Regex para detectar formato ISO (YYYY-MM-DDTHH:mm:ss.sssZ o similar)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

    // Función para formatear una fecha individual
    const formatSingleDate = (dateString) => {
      try {
        const date = new Date(dateString);

        // Verificar si es una fecha válida
        if (isNaN(date.getTime())) {
          return dateString; // Retornar original si no es válida
        }

        // Formatear: DD/MM/YYYY HH:mm
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return dateString; // Retornar original si hay error
      }
    };

    // Verificar si es un rango de fechas (separado por __)
    if (value.includes('__')) {
      const dates = value.split('__');

      // Verificar que ambas partes sean fechas válidas
      if (dates.length === 2 && dates.every(date => isoDateRegex.test(date.trim()))) {
        const startDate = formatSingleDate(dates[0].trim());
        const endDate = formatSingleDate(dates[1].trim());
        return `${startDate} - ${endDate}`;
      }
    }

    // Verificar si es una fecha individual
    if (isoDateRegex.test(value)) {
      return formatSingleDate(value);
    }

    // Si no es una fecha, retornar el valor original
    return value;
  };

  const CapturedInformation = () => {
    ////////// Obtaining report form label //////////
    const getFormLabel = (label, lang, default_value = "") => {
      if (typeof label === "string") {
        return label;
      } else {
        if (lang in label) {
          return label[lang];
        } else {
          return default_value;
        }
      }
    };

    useEffect(() => {
      const checkAdvisorReview = async () => {
        try {
          const response = await detailAdvisorReview(reportDetail.id);
          if (response && Object.keys(response).length > 0) {
            setIsShown(true);
          }
        } catch (error) { }
      };

      if (reportDetail?.id) {
        checkAdvisorReview();
      }
    }, [reportDetail]);

    return (
      <Stack spacing={2} paddingX={0.8} paddingY={1}>
        {reportDetail?.stepers?.map((item, index) => (
          // Section container
          <Box key={index}>
            {/* Section title */}
            <Box marginBottom={1.5}>
              <IconHeader
                key={index}
                iconName={
                  index === 0
                    ? "personal_id"
                    : index === 1
                      ? "outline_location"
                      : index === 2
                        ? "outline_note_search"
                        : "outline_direct_normal"
                }
                title={
                  item.locales ? getFormLabel(item.locales, lang) : item.title
                }
              />
            </Box>
            {/* Section Content */}
            <Stack
              sx={{ display: 'grid', 
                gridTemplateColumns: '1fr 1fr',
                gap: 2,
                p: 1, }}
            >
              {item?.form?.map((form, idx) => {
                let is_array = false;

                if (form.key === "subject" || form.key === "description") {
                  return <div key={idx}></div>;
                } else {
                  let the_label = form.label;

                  if (form.locale != undefined) {
                    the_label = form.locale;
                  }

                  let show_value = formatDateValue(form.text_value);

                  if (form.locales_value != null) {
                    show_value = getFormLabel(
                      form.locales_value,
                      lang,
                      show_value
                    );
                  }

                  if (Array.isArray(show_value)) {
                    const sorted = [...show_value].sort(
                      (a, b) => involvedOrder[a.type] - involvedOrder[b.type]
                    );

                    show_value = sorted.map((item, index) => {
                      const occupationDisplay = item.occupationLabel || item.occupation;
                      return (
                        <Typography
                          component="span"
                          key={index}
                          className={`involved ${item.type}`}
                        >
                          <Typography fontWeight={700}>
                            {t(`information.involved_${item.type}`)}:&nbsp;
                          </Typography>
                          {item.name} ({occupationDisplay})
                          {item.companyRelationshipLabel && ` - ${item.companyRelationshipLabel}`}
                          {item.areaLabel && ` - ${item.areaLabel}`}
                        </Typography>
                      );
                    });

                    is_array = true;
                  }
                  if (
                    typeof show_value === "boolean" ||
                    show_value === null ||
                    show_value === undefined
                  ) {
                    return null;
                  }

                  return (
                    <Stack className={is_array ? "involveds" : ""} key={idx}>
                      {/* Involved list */}
                      {is_array ? (
                        <Stack
                          className="involved_field"
                          sx={{display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          }}
                        >
                          <Typography variant="subtitle1" color="secondary">
                            {`${t("information.report_involveds")}:`}
                          </Typography>
                          <div className="--detail">{show_value}</div>
                        </Stack>
                      ) : (
                        // Form
                        <Stack key={idx}>
                          {/* Question */}
                          <Typography
                            fontWeight={600}
                            component="div"
                            color="secondary"
                            fontSize={12}
                          >
                            {getFormLabel(the_label, lang)}&nbsp;
                          </Typography>
                          {/* Answer */}
                          <Typography
                            variant="subtitle2"
                            component="div"
                            color={theme.palette.oxfordBlue.main}
                            borderBottom={`1px solid ${theme.palette.blueGray100.main}`}
                            fontSize={16}
                          >
                            {show_value ? stripLabelNumbering(String(show_value)) : "-----"}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  );
                }
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  const panels = [
    {
      id: "panel1",
      title: t("information.captured_information"),
      titleBackgrundColor: theme.palette.primary.main,
      titleColor: theme.palette.primary.contrastText,
      contentBorderColor: theme.palette.primary.main,
      content: <CapturedInformation />,
    },
    ...((reportDetail?.complements?.length > 0 && [
      {
        id: "panel3",
        title: `${t("information.report_complements")}`,
        titleBackgrundColor: theme.palette.blueGray100.main,
        titleColor: theme.palette.blueGray100.contrastText,
        contentBorderColor: theme.palette.blueGray100.main,
        content: <Complements complements={reportDetail?.complements} />,
      },
    ]) ||
      []),
    ...(isShown
      ? [
        {
          id: "panel2",
          title: t("AdvisorReview.advisor_review"),
          titleBackgrundColor: theme.palette.secondary.main,
          titleColor: theme.palette.secondary.contrastText,
          contentBorderColor: theme.palette.secondary.main,
          content: <AdvisorReview report_id={reportDetail.id} />,
        },
      ]
      : []),
  ];

  return (
    <div className="p-20 pt-[16]">
      <div style={{ float: "right", marginTop: 4 }}>
        <EGButton
          iconName="paperclip_2"
          label={`${reportDetail?.attachments.length === 0
              ? t("information.no_evidence")
              : t("information.evidence")
            }`}
          size="thin"
          onClick={() => setShowModalAttachments(true)}
          disabled={reportDetail.attachments.length === 0}
        />
      </div>

      <Typography
        variant="body2"
        color="blueGray600.main"
        component="div"
        display="flex"
        flexDirection="column"
        alignItems="start"
        justifyContent="space-between"
        fontSize={12}
      >
        <Box>
          {`${t("information.generated_report")}: ${parseDateToLgComun4(
            reportDetail?.created_at,
            t("information.locale")
          ) || t("information.no_updates")
            }`}
        </Box>
        <Box>{t("information.recived_by")}</Box>
      </Typography>

      <Typography variant="h6" className="mt-[5]" color="blueGray900">
        Folio: {reportDetail?.folio}
      </Typography>

      <Box
        className="investigation__header rounded dyTheme2 pt-[10] pe-[20] pb-[16] ps-[20]"
        mt={1.7}
        sx={{ border: `1px solid ${theme.palette.primary.main}` }}
      >
        <Typography
          variant="h5"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {reportDetail?.subject || t("information.no_subject")}
        </Typography>

        <Typography variant="h6" color="primary" className="mt-[15]">
          {`${t("information.report_description")}:`}
        </Typography>

        <Comments
          padding={0.5}
          maxLength={500}
          text={reportDetail?.description || t("information.no_description")}
          color="secondary"
          rounded
        />
      </Box>

      <div className="mt-20">
        <EGAccordion panels={panels} defaultExpanded="panel1" />
      </div>
    </div>
  );
};
